import { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { INDIAN_STATES } from '../config';
import SEO from '../components/SEO';
import { trackPixel } from '../lib/pixel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEALER_BG = "https://images.unsplash.com/photo-1713164835831-510bd6827993?w=1200&q=80";

export default function DealerPage() {
  const { t } = useLanguage();
  const dp = t.dealers_page;
  const d = t.dealers;

  const [form, setForm] = useState({
    name: '', business_name: '', phone: '', email: '',
    state: '', city: '', current_brands: '', message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.state || !form.city || !form.business_name) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/dealer-applications`, form);
      trackPixel('CompleteRegistration', { content_name: 'Dealer Application', status: 'submitted' });
      setSuccess(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-4 py-3 border rounded-lg text-sm font-body outline-none transition-all duration-200 focus:ring-2 bg-white";
  const inputStyle = { borderColor: '#E5E7EB' };
  const labelClass = "block text-xs font-semibold mb-1.5 uppercase tracking-wider";

  return (
    <main>
      <SEO
        title="Dealer Network — Become an ARV Dealer"
        path="/dealers"
        description="Join the ARV Machinery dealer network Pan-India. Apply to become an authorised dealer of ARV Rotavator, Disc Harrow, Mulcher and Cultivator with strong margins and factory support."
        keywords="ARV dealer, agricultural machinery dealership India, tractor implement dealer, rotavator distributor, farm equipment dealer network"
      />
      {/* Hero */}
      <section
        className="relative py-24"
        style={{ backgroundImage: `url(${DEALER_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(10,63,34,0.88)' }} />
        <div className="relative z-10 mx-auto max-w-3xl px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: '#FCD34D' }}>{d.badge}</span>
            <h1 className="font-heading font-black text-4xl sm:text-5xl text-white mt-2 mb-4">{dp.hero_title}</h1>
            <p className="text-white/70 text-lg">{dp.hero_subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16" style={{ backgroundColor: '#F9F8F4' }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <h2 className="font-heading font-bold text-3xl" style={{ color: '#0A3F22' }}>{dp.benefits_title}</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {d.benefits.map((b, i) => (
              <motion.div
                key={b}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="flex items-start gap-3 p-5 bg-white rounded-xl border card-hover"
                style={{ borderColor: '#E5E7EB' }}
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: '#EAF2EC' }}>
                  <CheckCircle size={16} style={{ color: '#0A3F22' }} />
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: '#1A1C1A' }}>{b}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-16" style={{ backgroundColor: '#0A3F22' }}>
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <h2 className="font-heading font-bold text-3xl text-white">{dp.process_title}</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {dp.process.map((step, i) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                className="text-center p-6 rounded-xl border"
                style={{ backgroundColor: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.1)' }}
              >
                <div className="font-heading font-black text-4xl mb-3" style={{ color: '#FCD34D' }}>{step.step}</div>
                <h3 className="font-heading font-bold text-white mb-2 text-sm">{step.title}</h3>
                <p className="text-xs leading-relaxed" style={{ color: '#A4C8AF' }}>{step.desc}</p>
                {i < dp.process.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 right-0 transform -translate-y-1/2 translate-x-4">
                    <span style={{ color: '#FCD34D' }}>→</span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#F9F8F4' }}>
        <div className="mx-auto max-w-2xl px-4 sm:px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="text-center mb-8">
              <h2 className="font-heading font-bold text-3xl" style={{ color: '#0A3F22' }}>{dp.form_title}</h2>
              <p className="mt-2 text-sm" style={{ color: '#4A554A' }}>Fill out the form and our team will contact you within 48 hours.</p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border p-8" style={{ borderColor: '#E5E7EB' }}>
              {success ? (
                <div className="text-center py-8">
                  <CheckCircle size={56} className="mx-auto mb-4" style={{ color: '#1B6B3D' }} />
                  <h3 className="font-heading font-bold text-xl mb-2" style={{ color: '#0A3F22' }}>Application Submitted!</h3>
                  <p className="text-sm" style={{ color: '#4A554A' }}>{dp.form.success}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="dealer-form" className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass} style={{ color: '#4A554A' }}>{dp.form.name} *</label>
                      <input data-testid="dealer-name" type="text" required value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Your full name" className={inputClass} style={inputStyle} />
                    </div>
                    <div>
                      <label className={labelClass} style={{ color: '#4A554A' }}>{dp.form.business} *</label>
                      <input data-testid="dealer-business" type="text" required value={form.business_name}
                        onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                        placeholder="Shop / business name" className={inputClass} style={inputStyle} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass} style={{ color: '#4A554A' }}>{dp.form.phone} *</label>
                      <input data-testid="dealer-phone" type="tel" required value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+91 XXXXX XXXXX" className={inputClass} style={inputStyle} />
                    </div>
                    <div>
                      <label className={labelClass} style={{ color: '#4A554A' }}>{dp.form.email}</label>
                      <input data-testid="dealer-email" type="email" value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="your@email.com" className={inputClass} style={inputStyle} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass} style={{ color: '#4A554A' }}>{dp.form.state} *</label>
                      <select data-testid="dealer-state" required value={form.state}
                        onChange={(e) => setForm({ ...form, state: e.target.value })}
                        className={inputClass} style={inputStyle}>
                        <option value="">Select state</option>
                        {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass} style={{ color: '#4A554A' }}>{dp.form.city} *</label>
                      <input data-testid="dealer-city" type="text" required value={form.city}
                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                        placeholder="Your city" className={inputClass} style={inputStyle} />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass} style={{ color: '#4A554A' }}>{dp.form.brands}</label>
                    <input data-testid="dealer-brands" type="text" value={form.current_brands}
                      onChange={(e) => setForm({ ...form, current_brands: e.target.value })}
                      placeholder="e.g. Mahindra, Sonalika..." className={inputClass} style={inputStyle} />
                  </div>

                  <div>
                    <label className={labelClass} style={{ color: '#4A554A' }}>{dp.form.message}</label>
                    <textarea data-testid="dealer-message" rows={4} value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Tell us about your business and why you want to partner with us..."
                      className={inputClass} style={inputStyle} />
                  </div>

                  {error && <p className="text-red-600 text-xs">{error}</p>}

                  <button
                    data-testid="dealer-submit"
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 rounded-lg text-white font-heading font-bold text-base transition-opacity hover:opacity-90 disabled:opacity-60"
                    style={{ backgroundColor: '#0A3F22' }}
                  >
                    {loading ? 'Submitting...' : dp.form.submit}
                  </button>

                  <p className="text-xs text-center" style={{ color: '#4A554A' }}>
                    By submitting, you agree to be contacted by our team regarding dealership opportunities.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
